/**
 * FilterPills Component
 *
 * Displays active search filters as removable pill badges.
 * Implements progressive search: L1 pills → L2 refinement → L2 pills
 *
 * SOLID Compliance:
 * - SRP: Only handles filter pill display and removal UI
 * - OCP: Extensible via props (customizable styling, icons)
 * - DIP: Depends on URLStateManager abstraction, not concrete implementation
 *
 * Location: apps/web/src/components/search/FilterPills.jsx
 * Updated: 2025-11-26 - MVP 1.3 Phase 2 - Progressive Search
 */

import React, { useMemo } from 'react';
import { X, Calendar, DollarSign, Star, MapPin as _MapPin, Users as _Users, Mountain } from 'lucide-react';
import useTaxonomy from '../../hooks/useTaxonomy';

/**
 * Icon mapping for filter types
 */
const FILTER_ICONS = {
  location: _MapPin,
  dates: Calendar,
  partySize: _Users,
  activitySlug: Mountain,
  priceRange: DollarSign,
  minRating: Star
};

/**
 * FilterPills Component
 * @param {Object} urlStateManager - URLStateManager instance
 * @param {boolean} showClearAll - Show "Clear all" button (default: true)
 * @param {string} className - Additional CSS classes
 */
export default function FilterPills({
  urlStateManager,
  showClearAll = true,
  className = ''
}) {
  // Load taxonomy for category name resolution
  const { taxonomy, loading: taxonomyLoading } = useTaxonomy();

  // Get current filter values
  const filters = urlStateManager.getFilters();

  // Get active filters (excluding category filters - we handle those separately)
  const activeFilters = urlStateManager.getActiveFiltersForDisplay()
    .filter(f => !['categoryL1', 'categoryL2', 'categoryL3'].includes(f.key));

  /**
   * Parse and resolve L1 category pills
   */
  const l1Pills = useMemo(() => {
    if (taxonomyLoading || !taxonomy || !filters.categoryL1) {
      return [];
    }

    const slugs = filters.categoryL1.split(',').map(s => s.trim());
    const pills = [];

    // Find each L1 category in taxonomy
    if (taxonomy.categories && Array.isArray(taxonomy.categories)) {
      slugs.forEach(slug => {
        const category = taxonomy.categories.find(c => c.slug === slug);
        if (category) {
          pills.push({
            slug,
            name: category.name,
            level: 'L1'
          });
        }
      });
    }

    return pills;
  }, [taxonomy, taxonomyLoading, filters.categoryL1]);

  /**
   * Parse and resolve L2 category pills
   */
  const l2Pills = useMemo(() => {
    if (taxonomyLoading || !taxonomy || !filters.categoryL2) {
      return [];
    }

    const slugs = filters.categoryL2.split(',').map(s => s.trim());
    const pills = [];

    // Find each L2 category in taxonomy
    if (taxonomy.categories && Array.isArray(taxonomy.categories)) {
      taxonomy.categories.forEach(l1 => {
        if (l1.children && Array.isArray(l1.children)) {
          l1.children.forEach(l2 => {
            if (slugs.includes(l2.slug)) {
              pills.push({
                slug: l2.slug,
                name: l2.name,
                parentName: l1.name,
                level: 'L2'
              });
            }
          });
        }
      });
    }

    return pills;
  }, [taxonomy, taxonomyLoading, filters.categoryL2]);

  /**
   * Parse and resolve L3 category pills
   */
  const l3Pills = useMemo(() => {
    if (taxonomyLoading || !taxonomy || !filters.categoryL3) {
      return [];
    }

    const slugs = filters.categoryL3.split(',').map(s => s.trim());
    const pills = [];

    // Find each L3 category in taxonomy
    if (taxonomy.categories && Array.isArray(taxonomy.categories)) {
      taxonomy.categories.forEach(l1 => {
        if (l1.children && Array.isArray(l1.children)) {
          l1.children.forEach(l2 => {
            if (l2.children && Array.isArray(l2.children)) {
              l2.children.forEach(l3 => {
                if (slugs.includes(l3.slug)) {
                  pills.push({
                    slug: l3.slug,
                    name: l3.name,
                    parentName: `${l1.name} > ${l2.name}`,
                    level: 'L3'
                  });
                }
              });
            }
          });
        }
      });
    }

    return pills;
  }, [taxonomy, taxonomyLoading, filters.categoryL3]);

  /**
   * Remove an L1 category with cascading child removal
   * When L1 is removed, also remove all its L2 and L3 children
   */
  const handleRemoveL1 = (slug) => {
    const l1Slugs = filters.categoryL1 ? filters.categoryL1.split(',').map(s => s.trim()) : [];
    const l2Slugs = filters.categoryL2 ? filters.categoryL2.split(',').map(s => s.trim()) : [];
    const l3Slugs = filters.categoryL3 ? filters.categoryL3.split(',').map(s => s.trim()) : [];

    const newL1Slugs = l1Slugs.filter(s => s !== slug);

    // Find children to remove (cascading)
    let newL2Slugs = [...l2Slugs];
    let newL3Slugs = [...l3Slugs];

    if (taxonomy && taxonomy.categories) {
      const l1Category = taxonomy.categories.find(cat => cat.slug === slug);
      if (l1Category && l1Category.children) {
        // Remove all L2 children
        const l2ChildSlugs = l1Category.children.map(l2 => l2.slug);
        newL2Slugs = newL2Slugs.filter(s => !l2ChildSlugs.includes(s));

        // Remove all L3 grandchildren
        const l3ChildSlugs = l1Category.children.flatMap(l2 =>
          (l2.children || []).map(l3 => l3.slug)
        );
        newL3Slugs = newL3Slugs.filter(s => !l3ChildSlugs.includes(s));
      }
    }

    // Update all three levels at once
    const updates = {};
    if (newL1Slugs.length > 0) {
      updates.categoryL1 = newL1Slugs.join(',');
    } else {
      updates.categoryL1 = undefined; // Clear from URL
    }
    if (newL2Slugs.length > 0) {
      updates.categoryL2 = newL2Slugs.join(',');
    } else {
      updates.categoryL2 = undefined; // Clear from URL
    }
    if (newL3Slugs.length > 0) {
      updates.categoryL3 = newL3Slugs.join(',');
    } else {
      updates.categoryL3 = undefined; // Clear from URL
    }

    urlStateManager.updateFilters(updates);
  };

  /**
   * Remove an L2 category with cascading child removal
   * When L2 is removed, also remove all its L3 children
   */
  const handleRemoveL2 = (slug) => {
    const l2Slugs = filters.categoryL2 ? filters.categoryL2.split(',').map(s => s.trim()) : [];
    const l3Slugs = filters.categoryL3 ? filters.categoryL3.split(',').map(s => s.trim()) : [];

    const newL2Slugs = l2Slugs.filter(s => s !== slug);

    // Find L3 children to remove (cascading)
    let newL3Slugs = [...l3Slugs];

    if (taxonomy && taxonomy.categories) {
      // Find the L2 category in the taxonomy tree
      for (const l1 of taxonomy.categories) {
        if (l1.children && Array.isArray(l1.children)) {
          const l2Category = l1.children.find(cat => cat.slug === slug);
          if (l2Category && l2Category.children) {
            // Remove all L3 children
            const l3ChildSlugs = l2Category.children.map(l3 => l3.slug);
            newL3Slugs = newL3Slugs.filter(s => !l3ChildSlugs.includes(s));
            break;
          }
        }
      }
    }

    // Update L2 and L3 levels
    const updates = {};
    if (newL2Slugs.length > 0) {
      updates.categoryL2 = newL2Slugs.join(',');
    } else {
      updates.categoryL2 = undefined; // Clear from URL
    }
    if (newL3Slugs.length > 0) {
      updates.categoryL3 = newL3Slugs.join(',');
    } else {
      updates.categoryL3 = undefined; // Clear from URL
    }

    urlStateManager.updateFilters(updates);
  };

  /**
   * Remove an L3 category
   */
  const handleRemoveL3 = (slug) => {
    const l3Slugs = filters.categoryL3 ? filters.categoryL3.split(',').map(s => s.trim()) : [];
    const newL3Slugs = l3Slugs.filter(s => s !== slug);

    if (newL3Slugs.length > 0) {
      urlStateManager.updateFilter('categoryL3', newL3Slugs.join(','));
    } else {
      urlStateManager.clearFilter('categoryL3');
    }
  };

  /**
   * Handle removing a single filter
   */
  const handleRemoveFilter = (filterKey) => {
    if (filterKey === 'dates') {
      urlStateManager.clearFilter('startDate');
      urlStateManager.clearFilter('endDate');
    } else if (filterKey === 'priceRange') {
      urlStateManager.clearFilter('minPrice');
      urlStateManager.clearFilter('maxPrice');
    } else {
      urlStateManager.clearFilter(filterKey);
    }
  };

  /**
   * Handle clearing all filters
   */
  const handleClearAll = () => {
    urlStateManager.clearAllFilters();
  };

  /**
   * Get icon component for filter
   */
  const getIcon = (filterKey) => {
    const IconComponent = FILTER_ICONS[filterKey];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : null;
  };

  /**
   * Format display label (remove emoji if icon component is used)
   */
  const formatLabel = (displayLabel) => {
    // eslint-disable-next-line security/detect-unsafe-regex
    return displayLabel.replace(/^[\u{1F300}-\u{1F9FF}]\s*/u, '');
  };

  // Don't render if no filters are active
  const hasFilters = activeFilters.length > 0 || l1Pills.length > 0 || l2Pills.length > 0 || l3Pills.length > 0;
  if (!hasFilters) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        {/* L1 Category Pills (Individual) */}
        {l1Pills.map((pill) => (
          <div
            key={`l1-${pill.slug}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full text-sm font-medium text-indigo-900 group"
          >
            <Mountain className="w-4 h-4" />
            <span>{pill.name}</span>
            <button
              onClick={(  _e) => {
                _e.stopPropagation();
                handleRemoveL1(pill.slug);
              }}
              className="flex items-center justify-center w-4 h-4 text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
              aria-label={`Remove ${pill.name} filter`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* L2 Category Pills (Individual) */}
        {l2Pills.map((pill) => (
          <div
            key={`l2-${pill.slug}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm font-medium text-purple-900 group"
          >
            <Mountain className="w-4 h-4" />
            <span>{pill.name}</span>
            <button
              onClick={(  _e) => {
                _e.stopPropagation();
                handleRemoveL2(pill.slug);
              }}
              className="flex items-center justify-center w-4 h-4 text-purple-600 hover:text-purple-800 transition cursor-pointer"
              aria-label={`Remove ${pill.name} filter`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* L3 Category Pills (Individual) */}
        {l3Pills.map((pill) => (
          <div
            key={`l3-${pill.slug}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-50 border border-pink-200 rounded-full text-sm font-medium text-pink-900 group"
          >
            <Mountain className="w-4 h-4" />
            <span>{pill.name}</span>
            <button
              onClick={(  _e) => {
                _e.stopPropagation();
                handleRemoveL3(pill.slug);
              }}
              className="flex items-center justify-center w-4 h-4 text-pink-600 hover:text-pink-800 transition cursor-pointer"
              aria-label={`Remove ${pill.name} filter`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Other Active Filter Pills */}
        {activeFilters.map((filter) => (
          <div
            key={filter.key}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-900 group"
          >
            {getIcon(filter.key)}
            <span>{formatLabel(filter.displayLabel)}</span>
            <button
              onClick={(  _e) => {
                _e.stopPropagation();
                handleRemoveFilter(filter.key);
              }}
              className="flex items-center justify-center w-4 h-4 text-blue-600 hover:text-blue-800 transition cursor-pointer"
              aria-label={`Remove ${filter.key} filter`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Clear All Button */}
        {showClearAll && (l1Pills.length + l2Pills.length + l3Pills.length + activeFilters.length) > 1 && (
          <button
            onClick={handleClearAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 underline transition"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
